
export default function PolicyInitiativesSection() {
  const initiatives = [
    {
      title: "Expanded Prescribing Authority",
      description: "Advocating for expanded prescribing authority for pharmacists across all Canadian provinces, enabling better patient care and healthcare accessibility.",
      status: "Active",
      progress: 75,
      provinces: ["BC", "AB", "ON", "QC"],
      icon: "ri-capsule-line",
      image: "https://public.readdy.ai/ai/img_res/f3a9e4320f9358b71c960817586bc4cc.jpg"
    },
    {
      title: "Veterinary Telehealth Expansion",
      description: "Securing regulatory approval for comprehensive veterinary telehealth services, improving rural animal healthcare access and emergency consultations.",
      status: "In Progress",
      progress: 60,
      provinces: ["All Provinces"],
      icon: "ri-video-line",
      image: "https://readdy.ai/api/search-image?query=Veterinarian%20conducting%20telehealth%20consultation%20with%20pet%20owner%20via%20computer%20screen%2C%20showing%20digital%20veterinary%20care%2C%20modern%20veterinary%20clinic%20with%20technology%20equipment%20and%20professional%20animal%20healthcare%20setting&width=400&height=300&seq=telehealth-expansion-001&orientation=landscape"
    },
    {
      title: "One Health Policy Framework",
      description: "Developing integrated healthcare policies that recognize the interconnection between human, animal, and environmental health for comprehensive care.",
      status: "Planning",
      progress: 30,
      provinces: ["National Initiative"],
      icon: "ri-earth-line",
      image: "https://readdy.ai/api/search-image?query=Collaborative%20healthcare%20meeting%20showing%20One%20Health%20approach%20with%20doctors%2C%20veterinarians%2C%20and%20environmental%20scientists%20working%20together%2C%20charts%20and%20graphs%20displaying%20integrated%20healthcare%20data%2C%20professional%20conference%20room%20setting&width=400&height=300&seq=one-health-policy-001&orientation=landscape"
    },
    {
      title: "Professional Scope Enhancement",
      description: "Working to expand professional scope of practice for both pharmacy and veterinary professionals to maximize their expertise in patient care.",
      status: "Active",
      progress: 85,
      provinces: ["BC", "AB", "SK", "MB", "ON"],
      icon: "ri-user-settings-line",
      image: "https://readdy.ai/api/search-image?query=Healthcare%20professionals%20including%20pharmacists%20and%20veterinarians%20collaborating%20in%20clinical%20setting%2C%20demonstrating%20expanded%20professional%20scope%20and%20interdisciplinary%20healthcare%20teamwork%2C%20modern%20medical%20facility%20environment&width=400&height=300&seq=scope-enhancement-001&orientation=landscape"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Planning':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-government-line text-2xl text-blue-600"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Key Policy Initiatives
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Driving meaningful change through strategic policy development and legislative advocacy across Canada
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {initiatives.map((initiative, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src={initiative.image} 
                alt={initiative.title}
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <i className={`${initiative.icon} text-lg text-blue-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {initiative.title}
                    </h3>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(initiative.status)}`}>
                    {initiative.status}
                  </span>
                </div>

                <p className="text-gray-600 mb-4">
                  {initiative.description}
                </p>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-500">{initiative.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${initiative.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500">Target Regions:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {initiative.provinces.map((province, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {province}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800 font-medium text-sm cursor-pointer whitespace-nowrap">
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
